// src/app/core/auth/oidc-auth.config.ts
import { HttpBackend, HttpClient } from '@angular/common/http';
import { map, shareReplay } from 'rxjs';
import {
  LogLevel,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader,
} from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import { OidcJson, INectaClientConfig } from '../shared/services/config-service';

// Stabile Flags, die unabhängig von der Umgebung gelten, sowie die
// Origin-abhängigen Werte (bleiben Code-Default, siehe AP6-Konstraint).
const DEFAULT_OIDC: OpenIdConfiguration = {
  // URL, zu der nach erfolgreicher Authentifizierung umgeleitet wird.
  redirectUrl: window.location.origin,
  // Verwendeter Response Type (hier "code" für Authorization Code Flow).
  responseType: 'code',
  // Aktiviert den stillen Token-Erneuerungsmechanismus.
  silentRenew: true,
  silentRenewUrl: window.location.origin + '/silent-renew.html',
  ignoreNonceAfterRefresh: true,
  // Aktiviert die Verwendung von Refresh Tokens.
  useRefreshToken: true,
  // Log-Level der Authentifizierungsbibliothek.
  logLevel: LogLevel.Warn,
  // Löst ein Event aus, wenn das Autorisierungsergebnis eintrifft.
  triggerAuthorizationResultEvent: true,
  // Route, zu der der Nutzer nach dem Login weitergeleitet wird.
  postLoginRoute: window.location.origin,
  // Route, zu der umgeleitet wird, wenn der Nutzer nicht berechtigt ist.
  forbiddenRoute: '/forbidden',
  // Route, zu der umgeleitet wird, wenn der Nutzer nicht authentifiziert ist.
  unauthorizedRoute: '/unauthorized',
  // Automatischer Abruf der User-Informationen nach erfolgreicher Authentifizierung.
  autoUserInfo: true,
  renewUserInfoAfterTokenRenew: true,
};

/**
 * Factory für den StsConfigLoader: lädt die umgebungsabhängige config.<env>.json
 * (dieselbe Datei wie der ConfigService) und spreadet deren "oidc"-Sektion über
 * DEFAULT_OIDC. Ein Profilwechsel (z.B. Authority local↔staging) wirkt damit
 * ohne Angular-Rebuild — es genügt, die gemountete JSON zu ändern.
 */
export function authConfigLoaderFactory(handler: HttpBackend): StsConfigLoader {
  // WICHTIG: Eigener HttpClient direkt auf dem HttpBackend, damit der Config-Request
  // die DI-Interceptor-Kette (u.a. TokenInterceptor) umgeht. Andernfalls würde das
  // Laden der OIDC-Config beim Bootstrap den TokenInterceptor auslösen, der via
  // OidcSecurityService.getAccessToken() wiederum genau diese Config benötigt –
  // ein Zirkel, der die App beim App-Initializer-Auth-Check hängen lässt (weißer Screen).
  const http = new HttpClient(handler);

  const config$ = http
    .get<INectaClientConfig>(`./assets/config/config.${environment.configEnv}.json`)
    .pipe(
      map((json) => {
        const oidc: OidcJson | undefined = json.oidc;
        if (!oidc) {
          throw new Error(
            `OIDC config missing in config.${environment.configEnv}.json`,
          );
        }
        return {
          ...DEFAULT_OIDC,
          ...oidc,
          redirectUrl: window.location.origin,
          silentRenewUrl: window.location.origin + '/silent-renew.html',
        } satisfies OpenIdConfiguration;
      }),
      // Der Loader wird von der Library mehrfach subscribed (checkAuth, getAccessToken …);
      // shareReplay verhindert wiederholte HTTP-Abrufe derselben Config.
      shareReplay(1),
    );

  return new StsConfigHttpLoader(config$);
}
